import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { formatBarcode } from '@persped/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllocateLabels } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Sorszám-tartomány foglalása nyomtatás NÉLKÜL — üzemeltetői vészkijárat.
 *
 * Ez teszi vállalhatóvá a `folder.barcode → label_allocation` idegen
 * kulcsot: ha a raktárban felbukkan egy KONKRÉT, a rendszer mellett már
 * kinyomtatott címke (pl. `MAP-00611`, miközben csak `MAP-00500`-ig van
 * kiosztva), ez ~10 másodperc alatt feloldja a regisztrációs blokádot.
 *
 * Ezért `fromSequence`/`toSequence` és nem darabszám: nem „adj néhány új
 * számot", hanem „vezesd be pontosan EZT a már létező tartományt".
 * Ütközésnél (a szám már ki van osztva) a hiba a HELYES válasz.
 */
export function AllocateLabelsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const allocate = useAllocateLabels();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');

  const fromNum = Number(from);
  const toNum = Number(to);
  const valid =
    Number.isInteger(fromNum) &&
    Number.isInteger(toNum) &&
    fromNum >= 1 &&
    toNum >= fromNum &&
    toNum <= 99_999 &&
    toNum - fromNum + 1 <= 5000 &&
    reason.trim().length > 0;

  const preview =
    Number.isInteger(fromNum) && Number.isInteger(toNum) && fromNum >= 1 && toNum >= fromNum
      ? `${formatBarcode('FOLDER', fromNum)} – ${formatBarcode('FOLDER', toNum)} (${toNum - fromNum + 1} db)`
      : null;

  const close = () => {
    allocate.reset();
    setFrom('');
    setTo('');
    setReason('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Sorszám-tartomány foglalása (nyomtatás nélkül)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Akkor használd, ha egy már kinyomtatott címke sorszáma nincs a rendszerben — ilyenkor a
            mappa regisztrációja „Ez a vonalkód nincs lefoglalva" hibával elakad. A foglalás után a
            regisztráció azonnal működik. Nyomtatás nem történik.
          </Alert>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Első sorszám"
              value={from}
              onChange={(e) => setFrom(e.target.value.replace(/\D/g, ''))}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              fullWidth
            />
            <TextField
              label="Utolsó sorszám"
              value={to}
              onChange={(e) => setTo(e.target.value.replace(/\D/g, ''))}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              fullWidth
            />
          </Stack>

          {preview && (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {preview}
            </Typography>
          )}

          <TextField
            label="Indoklás"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Az eseménynaplóba kerül — pl. „raktárban talált, korábban nyomtatott címkék”."
            fullWidth
            required
          />

          {allocate.isError && <ProblemAlert error={allocate.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          disabled={!valid || allocate.isPending}
          onClick={() => {
            allocate.mutate(
              { fromSequence: fromNum, toSequence: toNum, reason: reason.trim() },
              {
                onSuccess: (batch) => {
                  close();
                  void navigate(`/cimkek/${batch.id}`);
                },
              },
            );
          }}
        >
          Foglalás
        </Button>
      </DialogActions>
    </Dialog>
  );
}
